import { useMemo } from "react";
import type {
  BlockRendererMapping,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { isChildrenProp } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { useDeviceContext } from "../DeviceProvider";
import { isNonNullableUnknown } from "../Helpers/typePredicates";
import { flattenResponsiveStylesDeep } from "../Preview/flattenResponsiveStylesDeep";
import { RenderBlockList } from "./RenderBlockList";

export interface RendererProps {
  block: BaseBlock;
  components: BlockRendererMapping;
  path?: string;
}

export const Renderer = ({ block, components, path }: RendererProps) => {
  const device = useDeviceContext();

  // Memoize component lookup
  const Component = useMemo(
    () => components[block.type],
    [components, block.type],
  );

  if (!Component || block?.meta?.isHidden) return null;

  // Compute props without memoization to avoid React reconciliation issues
  const initialProps: BlockValues = {
    key: block.id,
  };

  const entries = Object.entries(block.props);

  const props = entries.reduce((props, [name, prop]) => {
    if (isChildrenProp(prop)) {
      props[name] = (
        <RenderBlockList
          blocks={prop.value}
          components={components}
          basePath={path ? `${path}.props.${name}.value` : undefined}
        />
      );
    } else if (isNonNullableUnknown(prop.value)) {
      if (name === "styles") {
        props[name] = flattenResponsiveStylesDeep(prop.value, device);
      } else {
        props[name] = prop.value;
      }
    }
    return props;
  }, initialProps);

  return (
    <Component
      data-block
      data-block-type={block.type}
      id={block.id}
      blockPath={path}
      blockInstanceMeta={block.meta}
      {...props}
    />
  );
};
